import React, { MouseEventHandler, PropsWithChildren, useCallback, useRef } from 'react';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { Icons } from '../../../../../components/atoms/SVG';
import Btn from '../../../../../components/molecules/Btn';
import { ICoordinates } from '../../../../../types/ICoordinates';
import { TooltipPlacement } from '../../../../../components/atoms/Tooltip';

type ConnectedPagesGraphPaginationProps = {
  numberOfChildPages: number;
  currentSetOfPages: number;
  maxPagesPerRow: number;
  coordinatesPreviousButton: ICoordinates;
  onPreviousButtonClick: MouseEventHandler<HTMLButtonElement>;
  coordinatesNextButton: ICoordinates;
  onNextButtonClick: MouseEventHandler<HTMLButtonElement>;
};

export function ConnectedPagesGraphPagination({
  numberOfChildPages,
  currentSetOfPages,
  maxPagesPerRow,
  coordinatesPreviousButton,
  onPreviousButtonClick,
  coordinatesNextButton,
  onNextButtonClick,
  children,
}: PropsWithChildren<ConnectedPagesGraphPaginationProps>) {
  const { t } = useSonTranslation('ConnectedPagesGraph');

  const previousButtonReference = useRef<HTMLDivElement>(null);
  const nextButtonReference = useRef<HTMLDivElement>(null);

  const handlePreviousButtonClick: MouseEventHandler<HTMLButtonElement> = e => {
    onPreviousButtonClick(e);
    requestAnimationFrame(() => {
      nextButtonReference.current?.querySelector('button')?.focus();
    });
  };

  const handleNextButtonClick: MouseEventHandler<HTMLButtonElement> = e => {
    onNextButtonClick(e);
    requestAnimationFrame(() => {
      previousButtonReference.current?.querySelector('button')?.focus();
    });
  };

  const hasMorePages = useCallback((): boolean => {
    const pagesCount = numberOfChildPages / maxPagesPerRow;
    return pagesCount > 1 && currentSetOfPages < Math.ceil(pagesCount) - 1;
  }, [numberOfChildPages, currentSetOfPages, maxPagesPerRow]);

  return (
    <>
      {currentSetOfPages > 0 && (
        <div
          className="connected-pages-graph__pagination-previous"
          ref={previousButtonReference}
          style={{
            /* stylelint-disable value-keyword-case */
            left: coordinatesPreviousButton.x,
            top: coordinatesPreviousButton.y,
            /* stylelint-disable value-keyword-case */
          }}
        >
          <Btn
            rel="prev"
            text={t('previous')}
            titleText={t('previous')}
            hiddenText
            svgSpriteId={Icons.ARROW_LEFT}
            tooltipPlacement={TooltipPlacement.TOP}
            // Events
            onClick={handlePreviousButtonClick}
          />
        </div>
      )}
      {children}
      {hasMorePages() && (
        <div
          className="connected-pages-graph__pagination-next"
          ref={nextButtonReference}
          style={{
            /* stylelint-disable value-keyword-case */
            left: coordinatesNextButton.x,
            top: coordinatesNextButton.y,
            /* stylelint-enable value-keyword-case */
          }}
        >
          <Btn
            rel="next"
            text={t('next')}
            titleText={t('next')}
            hiddenText
            svgSpriteId={Icons.ARROW_RIGHT}
            // Events
            onClick={handleNextButtonClick}
          />
        </div>
      )}
    </>
  );
}
